import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { MdAdd, MdCalendarMonth, MdChevronLeft, MdChevronRight, MdOpenInNew } from "react-icons/md";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import actividadesServiceGlobal from "services/actividadesService/actividades.service";
import { hoyLocal } from "utils/fechasLocales";
import FormularioActividad from "components/actividades/FormularioActividad";
import { COLOR_TIPO, ICONO_TIPO, TIPOS_POR_DEFECTO } from "components/actividades/actividades";
import {
  COLOR_ESTADO_DIA,
  DIAS_SEMANA_INICIAL,
  agruparPorFecha,
  diasDeLaCuadricula,
  esDelMes,
  estadoDelDia,
  mesDe,
  moverMes,
  nombreLargoDelDia,
  rangoDeLaCuadricula,
  tituloMes,
} from "components/actividades/calendario";

const catalogosIniciales = { tipos: TIPOS_POR_DEFECTO, asesores: [], puedeVerTodo: false };

export function MiniCalendario({ mes, seleccionado, hoy, porFecha, onSeleccionar, onCambiarMes }) {
  const textoPrincipal = useColorModeValue("secondaryGray.900", "white");
  const textoSecundario = useColorModeValue("gray.500", "gray.400");
  const fondoHover = useColorModeValue("gray.100", "whiteAlpha.200");
  const anilloHoy = useColorModeValue("brand.500", "brand.300");
  const dias = useMemo(() => diasDeLaCuadricula(mes), [mes]);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="8px">
        <IconButton size="sm" variant="ghost" borderRadius="full" aria-label="Mes anterior" icon={<MdChevronLeft />} onClick={() => onCambiarMes(-1)} />
        <Text fontWeight="700" color={textoPrincipal}>{tituloMes(mes)}</Text>
        <IconButton size="sm" variant="ghost" borderRadius="full" aria-label="Mes siguiente" icon={<MdChevronRight />} onClick={() => onCambiarMes(1)} />
      </Flex>
      <Grid templateColumns="repeat(7, 1fr)" gap="2px" textAlign="center">
        {DIAS_SEMANA_INICIAL.map((dia, indice) => (
          <Text key={`${dia}-${indice}`} fontSize="11px" fontWeight="700" color={textoSecundario} py="4px">{dia}</Text>
        ))}
        {dias.map((fecha) => {
          const delMes = esDelMes(fecha, mes);
          const esHoy = fecha === hoy;
          const elegido = fecha === seleccionado;
          const estado = estadoDelDia(porFecha.get(fecha), fecha, hoy);
          return (
            <Flex
              key={fecha}
              as="button"
              type="button"
              direction="column"
              align="center"
              justify="center"
              h="36px"
              borderRadius="10px"
              bg={elegido ? "brand.500" : "transparent"}
              color={elegido ? "white" : delMes ? textoPrincipal : textoSecundario}
              opacity={delMes ? 1 : 0.55}
              borderWidth={esHoy && !elegido ? "2px" : "0"}
              borderColor={anilloHoy}
              fontWeight={esHoy || elegido ? "700" : "500"}
              fontSize="sm"
              _hover={{ bg: elegido ? "brand.600" : fondoHover }}
              onClick={() => onSeleccionar(fecha)}
              aria-label={`${nombreLargoDelDia(fecha)}${estado ? `, con actividades` : ""}`}
              aria-pressed={elegido}
            >
              {Number(fecha.slice(8))}
              <Box
                w="5px"
                h="5px"
                mt="1px"
                borderRadius="full"
                bg={estado ? (elegido ? "white" : COLOR_ESTADO_DIA[estado]) : "transparent"}
                data-testid={estado ? `punto-${fecha}` : undefined}
              />
            </Flex>
          );
        })}
      </Grid>
    </Box>
  );
}

export default function CalendarioNavbar() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const navbarIcon = useColorModeValue("gray.500", "white");
  const fondoHover = useColorModeValue("gray.100", "whiteAlpha.200");
  const menuBg = useColorModeValue("white", "navy.800");
  const textoPrincipal = useColorModeValue("secondaryGray.900", "white");
  const textoSecundario = useColorModeValue("gray.600", "gray.300");
  const fondoItem = useColorModeValue("gray.50", "whiteAlpha.100");
  const shadow = useColorModeValue("14px 17px 40px 4px rgba(112, 144, 176, 0.18)", "14px 17px 40px 4px rgba(112, 144, 176, 0.06)");

  const hoy = hoyLocal();
  const [mes, setMes] = useState(() => mesDe(hoy));
  const [seleccionado, setSeleccionado] = useState(hoy);
  const [actividades, setActividades] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [catalogos, setCatalogos] = useState(catalogosIniciales);
  const [formularioAbierto, setFormularioAbierto] = useState(false);
  const [pendientesHoy, setPendientesHoy] = useState(0);

  // Pasar de mes rápido deja varias consultas en vuelo: solo vale la última.
  const consulta = useRef(0);

  const cargarMes = useCallback(async (mesVisible) => {
    const numero = ++consulta.current;
    const rango = rangoDeLaCuadricula(mesVisible);
    setCargando(true);
    const respuesta = await actividadesServiceGlobal.getCalendario(rango);
    if (numero !== consulta.current) return;
    setCargando(false);
    if (respuesta?.error) return;
    const datos = Array.isArray(respuesta?.data) ? respuesta.data : [];
    setActividades(datos);
    // El número del botón es de hoy: solo se recalcula si el mes que se pidió
    // incluye hoy, para que mirar otro mes no lo borre.
    const hoyActual = hoyLocal();
    if (rango.desde <= hoyActual && hoyActual <= rango.hasta) {
      setPendientesHoy(datos.filter((actividad) => actividad.fecha === hoyActual && !actividad.completada).length);
    }
  }, []);

  // Al montar solo para el número del botón; el resto se pide al abrir.
  useEffect(() => {
    cargarMes(mesDe(hoyLocal()));
  }, [cargarMes]);

  const abrir = () => {
    onOpen();
    const actual = mesDe(hoyLocal());
    setMes(actual);
    setSeleccionado(hoyLocal());
    cargarMes(actual);
    if (catalogos === catalogosIniciales) {
      actividadesServiceGlobal.getCatalogos().then((respuesta) => {
        if (respuesta?.error) return;
        setCatalogos({
          tipos: Array.isArray(respuesta?.tipos) && respuesta.tipos.length ? respuesta.tipos : TIPOS_POR_DEFECTO,
          asesores: Array.isArray(respuesta?.asesores) ? respuesta.asesores : [],
          puedeVerTodo: Boolean(respuesta?.puedeVerTodo),
        });
      });
    }
  };

  const cambiarMes = (delta) => {
    const siguiente = moverMes(mes, delta);
    setMes(siguiente);
    cargarMes(siguiente);
  };

  const porFecha = useMemo(() => agruparPorFecha(actividades), [actividades]);
  const delDia = porFecha.get(seleccionado) || [];

  const irAlCalendario = (fecha = seleccionado) => {
    onClose();
    navigate(`/admin/Calendario?fecha=${fecha}`);
  };

  return (
    <>
      <Popover isOpen={isOpen} onOpen={abrir} onClose={onClose} placement="bottom-end" isLazy>
        <PopoverTrigger>
          <Box position="relative">
            <Tooltip label="Calendario" hasArrow openDelay={400}>
              <IconButton
                aria-label={pendientesHoy ? `Calendario, ${pendientesHoy} pendientes hoy` : "Calendario"}
                icon={<Icon as={MdCalendarMonth} boxSize={{ base: "22px", md: "24px" }} />}
                variant="ghost"
                borderRadius="full"
                w={{ base: "38px", md: "42px" }}
                h={{ base: "38px", md: "42px" }}
                minW="auto"
                color={navbarIcon}
                _hover={{ bg: fondoHover }}
              />
            </Tooltip>
            {pendientesHoy > 0 && (
              <Flex
                position="absolute"
                top="2px"
                right="0px"
                minW="18px"
                h="18px"
                px="4px"
                borderRadius="999px"
                bg="brand.500"
                color="white"
                fontSize="10px"
                fontWeight="700"
                align="center"
                justify="center"
                borderWidth="2px"
                borderColor={menuBg}
                pointerEvents="none"
                data-testid="contador-calendario"
              >
                {pendientesHoy > 99 ? "99+" : pendientesHoy}
              </Flex>
            )}
          </Box>
        </PopoverTrigger>
        <PopoverContent bg={menuBg} border="none" boxShadow={shadow} borderRadius="20px" w={{ base: "calc(100vw - 24px)", md: "340px" }} maxW="calc(100vw - 24px)">
          <PopoverBody p="16px">
            <MiniCalendario
              mes={mes}
              seleccionado={seleccionado}
              hoy={hoy}
              porFecha={porFecha}
              onSeleccionar={setSeleccionado}
              onCambiarMes={cambiarMes}
            />

            <Box mt="14px">
              <Flex justify="space-between" align="baseline" mb="8px">
                <Text fontSize="sm" fontWeight="700" color={textoPrincipal} textTransform="capitalize">
                  {seleccionado === hoy ? "Hoy" : nombreLargoDelDia(seleccionado)}
                </Text>
                {delDia.length > 0 && <Badge borderRadius="full">{delDia.length}</Badge>}
              </Flex>
              {cargando && !actividades.length ? (
                <Skeleton h="44px" borderRadius="10px" />
              ) : delDia.length === 0 ? (
                <Text fontSize="sm" color={textoSecundario} py="6px">Sin actividades este día.</Text>
              ) : (
                <Flex direction="column" gap="6px" maxH="200px" overflowY="auto">
                  {delDia.slice(0, 8).map((actividad) => (
                    <Flex
                      key={actividad.id}
                      as="button"
                      type="button"
                      align="center"
                      gap="10px"
                      p="8px"
                      borderRadius="10px"
                      bg={fondoItem}
                      borderLeftWidth="3px"
                      borderLeftColor={`${COLOR_TIPO[actividad.tipo] || "gray"}.400`}
                      textAlign="left"
                      onClick={() => irAlCalendario(actividad.fecha)}
                      _hover={{ bg: fondoHover }}
                    >
                      <Icon as={ICONO_TIPO[actividad.tipo] || ICONO_TIPO.TAREA} color={`${COLOR_TIPO[actividad.tipo] || "gray"}.500`} flexShrink={0} />
                      <Box flex="1" minW={0}>
                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          color={textoPrincipal}
                          noOfLines={1}
                          textDecoration={actividad.completada ? "line-through" : "none"}
                          opacity={actividad.completada ? 0.7 : 1}
                        >
                          {actividad.asunto}
                        </Text>
                        {actividad.cliente?.nombre && (
                          <Text fontSize="xs" color={textoSecundario} noOfLines={1}>{actividad.cliente.nombre}</Text>
                        )}
                      </Box>
                      <Text fontSize="xs" fontWeight="700" color={textoSecundario} flexShrink={0}>{actividad.hora || ""}</Text>
                    </Flex>
                  ))}
                  {delDia.length > 8 && (
                    <Text fontSize="xs" color={textoSecundario} textAlign="center">y {delDia.length - 8} más</Text>
                  )}
                </Flex>
              )}
            </Box>

            <Flex gap="8px" mt="14px">
              <Button
                size="sm"
                flex="1"
                leftIcon={<MdAdd />}
                onClick={() => {
                  onClose();
                  setFormularioAbierto(true);
                }}
              >
                Nueva actividad
              </Button>
              <Button size="sm" flex="1" variant="brand" rightIcon={<MdOpenInNew />} onClick={() => irAlCalendario()}>
                Ver calendario
              </Button>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      <FormularioActividad
        isOpen={formularioAbierto}
        onClose={() => setFormularioAbierto(false)}
        valoresIniciales={{ fecha: seleccionado, tipo: "TAREA" }}
        catalogos={catalogos}
        onGuardada={(_, mensaje) => {
          setFormularioAbierto(false);
          toast({ status: "success", title: mensaje || "Actividad guardada", duration: 3500, isClosable: true, position: "top-right" });
          cargarMes(mes);
        }}
      />
    </>
  );
}
